import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMobileScreen, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

const UserWhatsAppSettings = () => {
  const { locationId, userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [error, setError] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  const [sharingMode, setSharingMode] = useState('SHARED');

  useEffect(() => {
    fetchUserInfo();
    fetchLocationInfo();
    fetchQRCode();
    
    // Auto-refresh QR code every 5 seconds
    const interval = setInterval(() => {
      fetchQRCode();
    }, 5000);
    
    setRefreshInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [locationId, userId]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  const fetchLocationInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/whatsapp/sessions/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSharingMode(data.sharingMode || 'SHARED');
        const user = data.userSessions.find(u => u.userId === userId);
        setLocationInfo({
          locationId: data.locationId,
          locationName: data.locationName,
          userName: user?.name,
          userEmail: user?.email,
        });
      }
    } catch (err) {
      console.error('Error fetching location info:', err);
    }
  };

  const fetchQRCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/qr/${locationId}/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.hasQR && data.qrCode) {
          setQrCode(data.qrCode);
          setError('');
        } else {
          setQrCode(null);
          setError(data.message || 'No QR code available');
        }
      } else if (response.status === 403) {
        setError('Unauthorized to view this QR code');
      } else {
        setError('Failed to fetch QR code');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error while fetching QR code');
    } finally {
      setLoading(false);
    }
  };

  const initializeWhatsApp = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/init-user/${locationId}/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setError('');
        // Wait 2 seconds then fetch QR code
        setTimeout(() => fetchQRCode(), 2000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to initialize WhatsApp');
      }
    } catch (err) {
      setError('Network error while initializing WhatsApp');
    }
  };

  if (loading) {
    return (
      <DashboardLayout userInfo={userInfo}>
        <div className="flex justify-center items-center min-h-screen">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  return (
    <DashboardLayout userInfo={userInfo} onLogout={handleLogout}>
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => navigate(`/user-management/${locationId}`)}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-900 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to User Management
        </button>

        <div className="glass-card rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }}>
          <div className="mb-6 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #1e1b4b, #3730a3)' }}>
              <FontAwesomeIcon icon={faMobileScreen} className="text-white text-2xl" />
            </div>
            <h1 className="text-2xl font-bold mb-2">WhatsApp QR Code</h1>
            {locationInfo && (
              <div className="text-gray-600 space-y-1">
                <p className="text-sm">Location: {locationId}</p>
                <p className="font-semibold">{locationInfo.userName}</p>
                <p className="text-sm">{locationInfo.userEmail}</p>
                <div className="mt-3">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    sharingMode === 'SHARED' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {sharingMode} MODE
                  </span>
                </div>
              </div>
            )}
          </div>

        {error && !qrCode && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-yellow-600 text-lg mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-800">No QR Code Available</p>
                <p className="text-sm text-yellow-700 mt-1">{error}</p>
                <button
                  onClick={initializeWhatsApp}
                  className="mt-4 btn-primary text-sm"
                >
                  Initialize WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}

          {qrCode && (
            <div className="flex flex-col items-center">
              {sharingMode === 'SHARED' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 w-full max-w-md">
                  <p className="text-sm text-purple-800 text-center">
                    <strong>Shared Mode:</strong> This QR code is shared across all users in this location.
                  </p>
                </div>
              )}
              
              <div className="bg-white p-4 rounded-2xl shadow-inner border border-gray-100 mb-4">
                <img
                  src={qrCode}
                  alt="WhatsApp QR Code"
                  className="w-72 h-72 rounded-xl"
                />
              </div>            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                <strong>Steps to connect:</strong>
              </p>
              <ol className="text-sm text-gray-600 text-left max-w-md mx-auto space-y-1">
                <li>1. Open WhatsApp on your phone</li>
                <li>2. Go to Settings → Linked Devices</li>
                <li>3. Tap "Link a Device"</li>
                <li>4. Scan this QR code</li>
              </ol>
            </div>

            <div className="mt-6 text-xs text-gray-500">
              QR code refreshes automatically every 5 seconds
            </div>
          </div>
        )}

        {error && qrCode && (
          <div className="mt-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-center">
            <p className="text-sm">{error}</p>
          </div>
        )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserWhatsAppSettings;
